import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { UserPlus, Trash2, Edit, Search, X, Save, Download, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import BadgePhotoModal from "../components/BadgePhotoModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const SortHeader = ({ label, field, sortField, sortDir, onSort }) => {
  const active = sortField === field;
  return (
    <th
      className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-[#4A5568] cursor-pointer select-none hover:text-[#08707A] transition-colors whitespace-nowrap"
      onClick={() => onSort(field)}
      data-testid={`sort-${field}`}
    >
      <span className="inline-flex items-center gap-1.5">
        {label}
        {active ? (
          sortDir === "asc" ? <ArrowUp size={13} className="text-[#1CA3B0]" /> : <ArrowDown size={13} className="text-[#1CA3B0]" />
        ) : (
          <ArrowUpDown size={13} className="text-slate-300" />
        )}
      </span>
    </th>
  );
};

const AdminLeads = () => {
  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [sortField, setSortField] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", city: "", profession: "", referral_source: "", referral_detail: "", notes: "" });
  const [viewingBadge, setViewingBadge] = useState(null);

  useEffect(() => { fetchLeads(); }, []);

  const fetchLeads = async () => {
    try { const res = await axios.get(`${API}/leads`); setLeads(res.data); } catch (err) { console.error(err); }
  };

  const resetForm = () => {
    setForm({ first_name: "", last_name: "", email: "", phone: "", city: "", profession: "", referral_source: "", referral_detail: "", notes: "" });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) { await axios.put(`${API}/leads/${editingId}`, form); }
      else { await axios.post(`${API}/leads`, form); }
      fetchLeads();
      resetForm();
    } catch (err) { console.error(err); }
  };

  const handleEdit = (lead) => {
    setForm({
      first_name: lead.first_name || "", last_name: lead.last_name || "",
      email: lead.email || "", phone: lead.phone || "", city: lead.city || "",
      profession: lead.profession || "",
      referral_source: lead.referral_source || "", referral_detail: lead.referral_detail || "",
      notes: lead.notes || "",
    });
    setEditingId(lead.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Jeni te sigurt qe doni ta fshini kete lead?")) return;
    try { await axios.delete(`${API}/leads/${id}`); setLeads(leads.filter((l) => l.id !== id)); } catch (err) { console.error(err); }
  };

  const handleExport = () => {
    const csv = ["Nr,Emri,Mbiemri,Nr.Cel,Email,Profesioni,Data Regjistrimi,Burimi,Detaje,Qyteti,Shenime"].concat(
      sorted.map((l, i) => `${i + 1},"${l.first_name}","${l.last_name}","${l.phone}","${l.email}","${l.profession || ''}","${formatDate(l.created_at)}","${l.referral_source}","${l.referral_detail}","${l.city}","${l.notes}"`)
    ).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "leads.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const handleSort = (field) => {
    if (sortField === field) { setSortDir(sortDir === "asc" ? "desc" : "asc"); }
    else { setSortField(field); setSortDir("asc"); }
  };

  const formatDate = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
  };

  const filtered = leads.filter((l) => {
    const s = search.toLowerCase();
    return !s || `${l.first_name} ${l.last_name} ${l.email} ${l.phone} ${l.city} ${l.profession} ${l.referral_source} ${l.referral_detail}`.toLowerCase().includes(s);
  });

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let valA = a[sortField] || "";
      let valB = b[sortField] || "";
      if (sortField === "created_at") { valA = new Date(valA); valB = new Date(valB); }
      else { valA = String(valA).toLowerCase(); valB = String(valB).toLowerCase(); }
      if (valA < valB) return sortDir === "asc" ? -1 : 1;
      if (valA > valB) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [filtered, sortField, sortDir]);

  const sourceColors = {
    "Regjistrim Eventi": { bg: "#E6F7F8", text: "#08707A" },
    "Forme Kontakti": { bg: "#E8EEF4", text: "#003F72" },
    "Manual": { bg: "#F0FAF0", text: "#2D6A2E" },
    "Referral": { bg: "#FEF3E2", text: "#9A6400" },
    "Social Media": { bg: "#F3E8FF", text: "#6B21A8" },
    "Website": { bg: "#FEE2E2", text: "#991B1B" },
  };

  return (
    <div data-testid="admin-leads">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Inter_Tight']">Leads</h1>
          <p className="text-sm text-[#828D9E] mt-1">{leads.length} leads totale</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleExport} className="rounded-lg px-4 py-2 text-sm font-semibold border border-slate-200 text-[#4A5568] hover:bg-slate-50 inline-flex items-center gap-2" data-testid="export-leads-btn">
            <Download size={14} /> Eksporto CSV
          </button>
          <button onClick={() => { resetForm(); setShowForm(true); }} className="rounded-lg px-5 py-2 text-sm font-semibold text-white inline-flex items-center gap-2 bg-[#08707A] hover:bg-[#065A63]" data-testid="add-lead-btn">
            <UserPlus size={14} /> Shto Lead
          </button>
        </div>
      </div>

      <div className="relative mb-5">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#828D9E]" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Kerko sipas emrit, emailit, profesionit..." className="pl-9 rounded-lg border-slate-200 bg-white" data-testid="search-leads" />
      </div>

      {/* Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={resetForm}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="lead-form-modal">
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <h3 className="text-lg font-bold">{editingId ? "Edito Lead" : "Shto Lead te ri"}</h3>
              <button onClick={resetForm} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs font-semibold mb-1 block">Emri *</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="rounded-lg border-slate-200 bg-slate-50" data-testid="lead-first-name" /></div>
                <div><Label className="text-xs font-semibold mb-1 block">Mbiemri *</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="rounded-lg border-slate-200 bg-slate-50" data-testid="lead-last-name" /></div>
              </div>
              <div><Label className="text-xs font-semibold mb-1 block">Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-lg border-slate-200 bg-slate-50" data-testid="lead-email" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs font-semibold mb-1 block">Telefon</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-lg border-slate-200 bg-slate-50" data-testid="lead-phone" /></div>
                <div><Label className="text-xs font-semibold mb-1 block">Profesioni</Label><Input value={form.profession} onChange={(e) => setForm({ ...form, profession: e.target.value })} placeholder="psh. Dentist, Ortodont" className="rounded-lg border-slate-200 bg-slate-50" data-testid="lead-profession" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs font-semibold mb-1 block">Qyteti</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="rounded-lg border-slate-200 bg-slate-50" data-testid="lead-city" /></div>
                <div>
                  <Label className="text-xs font-semibold mb-1 block">Burimi (Referral)</Label>
                  <select value={form.referral_source} onChange={(e) => setForm({ ...form, referral_source: e.target.value })} className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm" data-testid="lead-referral-source">
                    <option value="">Zgjidh...</option>
                    <option value="Regjistrim Eventi">Regjistrim Eventi</option>
                    <option value="Forme Kontakti">Forme Kontakti</option>
                    <option value="Manual">Manual</option>
                    <option value="Referral">Referral</option>
                    <option value="Social Media">Social Media</option>
                    <option value="Website">Website</option>
                  </select>
                </div>
              </div>
              <div><Label className="text-xs font-semibold mb-1 block">Detaje Burimi</Label><Input value={form.referral_detail} onChange={(e) => setForm({ ...form, referral_detail: e.target.value })} placeholder="psh. Emri i eventit / kursit" className="rounded-lg border-slate-200 bg-slate-50" data-testid="lead-referral-detail" /></div>
              <div><Label className="text-xs font-semibold mb-1 block">Shenime</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="rounded-lg border-slate-200 bg-slate-50" data-testid="lead-notes" /></div>
              <div className="flex gap-3 pt-2">
                <button type="submit" className="rounded-lg px-6 py-2.5 text-sm font-semibold text-white inline-flex items-center gap-2" style={{ background: "linear-gradient(130deg, #3CD4E1 0%, #003F72 100%)" }} data-testid="save-lead-btn"><Save size={14} /> Ruaj</button>
                <button type="button" onClick={resetForm} className="rounded-lg px-6 py-2.5 text-sm font-semibold border border-slate-200 text-[#4A5568]">Anulo</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="leads-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-[#4A5568] w-12">Nr</th>
                <SortHeader label="Emri" field="first_name" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label="Mbiemri" field="last_name" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label="Nr. Cel" field="phone" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label="Email" field="email" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label="Profesioni" field="profession" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label="Qyteti" field="city" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-[#4A5568] w-20">Badge</th>
                <SortHeader label="Date Regjistrimi" field="created_at" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label="Burimi" field="referral_source" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-[#4A5568] w-24">Veprime</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sorted.length === 0 ? (
                <tr><td colSpan={11} className="text-center py-16 text-[#828D9E]">Nuk ka leads.</td></tr>
              ) : sorted.map((lead, idx) => {
                const sc = sourceColors[lead.referral_source] || { bg: "#F1F5F9", text: "#64748B" };
                return (
                  <tr key={lead.id} className="hover:bg-slate-50/50 transition-colors" data-testid={`lead-row-${lead.id}`}>
                    <td className="px-4 py-3 text-sm text-[#828D9E] font-mono">{idx + 1}</td>
                    <td className="px-4 py-3 text-sm font-semibold text-[#0A0D10]">{lead.first_name}</td>
                    <td className="px-4 py-3 text-sm text-[#0A0D10]">{lead.last_name}</td>
                    <td className="px-4 py-3 text-sm text-[#4A5568] whitespace-nowrap">{lead.phone || "-"}</td>
                    <td className="px-4 py-3 text-sm text-[#4A5568]">{lead.email}</td>
                    <td className="px-4 py-3 text-sm text-[#4A5568]">{lead.profession || "-"}</td>
                    <td className="px-4 py-3 text-sm text-[#4A5568]">{lead.city || "-"}</td>
                    <td className="px-4 py-3">
                      {lead.badge_photo ? (
                        <button
                          type="button"
                          onClick={() => setViewingBadge({ src: lead.badge_photo, name: `${lead.first_name} ${lead.last_name}` })}
                          title="Shiko foton e badge-it"
                          className="rounded-lg overflow-hidden focus:outline-none focus:ring-2 focus:ring-[#1CA3B0]"
                          data-testid={`view-lead-badge-${lead.id}`}
                        >
                          <img src={lead.badge_photo.startsWith("http") ? lead.badge_photo : `${BACKEND_URL}${lead.badge_photo}`} alt="Badge" className="w-10 h-10 object-cover border border-slate-200 hover:ring-2 hover:ring-[#1CA3B0] transition-all" />
                        </button>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-[#4A5568] whitespace-nowrap">{formatDate(lead.created_at)}</td>
                    <td className="px-4 py-3">
                      <div>
                        <span className="inline-block text-[11px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap" style={{ backgroundColor: sc.bg, color: sc.text }}>
                          {lead.referral_source || "-"}
                        </span>
                        {lead.referral_detail && <p className="text-[10px] text-[#828D9E] mt-1 truncate max-w-[160px]">{lead.referral_detail}</p>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => handleEdit(lead)} className="p-1.5 text-[#08707A] hover:bg-[#08707A]/10 rounded-md transition-colors" data-testid={`edit-lead-${lead.id}`}><Edit size={15} /></button>
                        <button onClick={() => handleDelete(lead.id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-md transition-colors" data-testid={`delete-lead-${lead.id}`}><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <BadgePhotoModal src={viewingBadge?.src} name={viewingBadge?.name} onClose={() => setViewingBadge(null)} />
    </div>
  );
};

export default AdminLeads;
